import React from 'react';
import { Suspense, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PublicRoutes from './PublicRoutes';
import PrivateRoute from './PrivateRoute';
import AppLayout from '../Layout/Layout';

import SignUps from '../Auth/SignUps';
import Logins from '../Auth/Logins';
import SignUpComplete from '../Auth/SignUpComplete';

import Dashboard from '../Components/Customer/Dashboard';
import Customer from '../Components/Customer/Customer';
import ProductSuggestion from '../Components/Customer/ProductSuggestion';

import CartScreen from '../Components/cart/CartScreen';
import CreatePassword from '../Components/Customer/CreatePassword';
import RequestCreatePasswordLink from '../Components/Customer/RequestCreatePasswordLink';

import Loader from '../Layout/Loader';
import i18n from 'i18next';
import { isSupportedLanguage, normalizeLanguageCode } from '../utils/language';
import { storeReferralCodeFromUrl } from '../utils/referral';

import { Navigate, Route, Routes } from 'react-router-dom';
import Home from '../Components/Home';
import ChangePassword from '../Components/Customer/ChangePassword';
import Profile from '../Components/Customer/Profile';
import CustomerAddress from '../Components/Customer/CustomerAddress';
import CustomerSubscriptions from '../Components/Customer/CustomerSubscriptions';
import CustomerOrder from '../Components/Customer/CustomerOrder';
import OrderDetails from '../Components/Customer/OrderDetails';

import CustomerInquiry from '../Components/Customer/CustomerInquiry';

import ReferralCode from '../Components/Customer/ReferralCode';
import ScrollToTop from './ScrollToTop';
import CustomerCheckIn from '../Components/Customer/CustomerCheckIn';
import CustomerService from '../Components/Customer/CustomerService';
import Retake from '../Components/Customer/Retake';
import QuizComparison from '../Components/Customer/QuizComparison';
import QuizComparisonMessage from '../Components/Customer/QuizComparisonMessage';
import ForgetPassword from '../Auth/ForgetPassword';
import ResetYourPassword from '../Auth/ResetPassword';
import MagicLogin from '../Auth/MagicLogin';
import MagicLoginCallback from '../Auth/MagicLoginCallback';
import RiisePartner from '../Components/riisePartner/RiisePartner';
import LanguageEnforcer from './Language';
import GetTested from '../Components/riisePartner/getTested/GetTested';
import PractitionersWebinar from '../Components/PractitionersWebinar';
import PractitionersWebinarThankYou from '../Components/PractitionersWebinarThankYou';
import PostPurchaseLayout from '../Layout/PostPurchaseLayout';
import HowToTake from '../Components/HowToTake/HowToTake';
import ReminderGuide from '../Components/Guides/ReminderGuide';
import QRRedirect from '../Components/QRRedirect';
import ClassmedAppDownload from '../Components/pages/ClassmedAppDownload';
import PillsOrSachet from '../Components/pages/PillsOrSachet';
import StopGuessing from '../Components/pages/StopGuessing';
import PrequizGuesswork from '../Components/pages/Prequiz/Guesswork';
import PrequizWechsel from '../Components/pages/Prequiz/Wechsel';
import WrongCombination from '../Components/pages/WrongCombination';
import Wechsel from '../Components/pages/Wechsel';
import WechselListicle from '../Components/pages/WechselListicle';
import Wechsel2 from '../Components/pages/Wechsel2';
import Wechsel3 from '../Components/pages/Wechsel3';
import Referral from '../Components/pages/Referral';
import ReviewsPage from '../Components/pages/Reviews';
import { isClassmedMode } from '../utils/classmed';
import { setLanguage } from '../features/langSlice';

// Apply lang from URL before any component renders
const urlParams = new URLSearchParams(window.location.search);
const urlLang = urlParams.get('lang');
if (isClassmedMode()) {
  localStorage.setItem('lang', 'fr');
} else if (urlLang && isSupportedLanguage(urlLang)) {
  localStorage.setItem('lang', normalizeLanguageCode(urlLang));
}
storeReferralCodeFromUrl(window.location.search);

const Routers = () => {
  //const [login, setLogin] = useState(false);
  const dispatch = useDispatch();
  const isClassmed = isClassmedMode();
  const classmedQuizPath = `/classmed/quiz${window.location.search || ''}`;

  useEffect(() => {
    let abortController = new AbortController();

    //setLogin  useState(JSON.parse(localStorage.getItem('login')))[0];
    //setLogin(JSON.parse(localStorage.getItem('login')));

    const lang = normalizeLanguageCode(localStorage.getItem('lang'));
    i18n.changeLanguage(lang);

    const canControlScrollRestoration = 'scrollRestoration' in window.history;
    if (canControlScrollRestoration) {
      window.history.scrollRestoration = 'manual';
    }

    window.scrollTo(0, 0);

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    if (!isClassmed) return;
    localStorage.setItem('lang', 'fr');
    i18n.changeLanguage('fr');
    dispatch(setLanguage({ lng: 'fr' }));
  }, [dispatch, isClassmed]);

  return (
    <>
      <Suspense fallback={<Loader />}>
        <LanguageEnforcer>
          <Routes>
            <Route element={<AppLayout />}>
              <Route path={'/'} element={isClassmed ? <Navigate to={classmedQuizPath} replace /> : <Home />} />
            </Route>

            <Route path={'/*'} element={<PublicRoutes />} />

            <Route path={'/'} element={<PrivateRoute />}>
              <Route element={<AppLayout />}>
                <Route path={`/dashboard`} element={<Dashboard />} />
                <Route path={`/customer`} element={<Customer />} />
                <Route path={`/customer/askspecialist`} element={<CustomerInquiry />} />
                <Route path={'/customer/benefits'} element={<Navigate to="/dashboard" replace />} />
                <Route path={'/customer/service'} element={<CustomerService />} />
                <Route path={'/customer/retaketest'} element={<Retake />} />

                <Route path={`/customer/checkin`} element={<CustomerCheckIn />} />

                <Route path={`/cart`} element={<CartScreen />} />

                <Route path={'/dashboard/profile'} element={<Profile />} />
                <Route path={'/dashboard/password'} element={<ChangePassword />} />
                <Route path={'/dashboard/address'} element={<CustomerAddress />} />
                <Route path={'/dashboard/subscription'} element={<CustomerSubscriptions />} />
                <Route path={'/dashboard/orders'} element={<CustomerOrder />} />
                <Route path={'/dashboard/orderDetails'} element={<OrderDetails />} />
                <Route path={'/dashboard/referralcode'} element={<ReferralCode />} />
                <Route path={'/dashboard/comparison'} element={<QuizComparison />} />
                <Route path={'dashboard/comparison/message'} element={<QuizComparisonMessage />} />
              </Route>
            </Route>

            <Route element={<AppLayout />}>
              <Route exact path={`/product-suggestion`} element={<ProductSuggestion />} />
            </Route>

            <Route exact path={`/login`} element={<Logins />} />
            <Route exact path={`/magic-login`} element={<MagicLogin />} />
            <Route exact path={`/magic-login/callback`} element={<MagicLoginCallback />} />
            <Route exact path={`/signup`} element={<SignUps />} />
            <Route exact path={`/forgot-password`} element={<ForgetPassword />} />
            <Route exact path={`/reset-password`} element={<ResetYourPassword />} />
            <Route exact path={`/signup/complete`} element={<SignUpComplete />} />
            <Route path={`/createpassword`} element={<CreatePassword />} />
            <Route path={`/request-create-password-link`} element={<RequestCreatePasswordLink />} />
            <Route exact path={`/carrotcare`} element={<RiisePartner />} />
            <Route exact path={`/gettested`} element={<GetTested />} />
            <Route exact path={`/practitioners/webinar`} element={<PractitionersWebinar />} />
            <Route exact path={`/practitioners/webinar-ty`} element={<PractitionersWebinarThankYou />} />
            <Route element={<PostPurchaseLayout />}>
              <Route exact path={`/how-to-take`} element={<HowToTake />} />
              <Route exact path={`/guide/reminder`} element={<ReminderGuide />} />
            </Route>
            <Route exact path={`/insert-qr-code-1`} element={<QRRedirect to="/how-to-take" />} />
            <Route exact path={`/insert-qr-code-classmed1`} element={<QRRedirect to="https://www.weriise.co/how-to-take?lang=fr" />} />
            <Route exact path={`/insert-qr-code-classmed2`} element={<ClassmedAppDownload />} />

            {/* Landing pages — no AppLayout, no global nav */}
            <Route exact path={`/pages/pills-or-sachet`} element={<PillsOrSachet />} />
            <Route exact path={`/pages/stop-guessing`} element={<StopGuessing />} />
            <Route exact path={`/prequiz/guesswork`} element={<PrequizGuesswork />} />
            <Route exact path={`/prequiz/wechsel`} element={<PrequizWechsel />} />
            <Route exact path={`/pages/wrong-combination`} element={<WrongCombination />} />
            <Route exact path={`/pages/wechsel`} element={<Wechsel />} />
            <Route exact path={`/pages/wechsel-listicle`} element={<WechselListicle />} />
            <Route exact path={`/pages/wechsel-2`} element={<Wechsel2 />} />
            <Route exact path={`/pages/wechsel-3`} element={<Wechsel3 />} />
            <Route exact path={`/referral`} element={<Referral />} />
            <Route exact path={`/reviews`} element={<ReviewsPage />} />
          </Routes>
        </LanguageEnforcer>
      </Suspense>

      <ScrollToTop />
    </>
  );
};

export default Routers;
